import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Chip,
  Typography,
  IconButton,
  Alert,
  Divider,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { X, Save, AlertCircle } from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import dayjs from 'dayjs';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ProjectStatus, Project, TaskPriority } from '@/types';
import { projectService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';

// Priority mapping from TaskPriority enum to form values
const priorityEnumToFormValue = (priority: TaskPriority | string | undefined): 'low' | 'medium' | 'high' | 'critical' => {
  const mapping: Record<string, 'low' | 'medium' | 'high' | 'critical'> = {
    [TaskPriority.P1]: 'critical',
    [TaskPriority.P2]: 'high',
    [TaskPriority.P3]: 'medium',
    [TaskPriority.P4]: 'low',
    // Also handle string values
    critical: 'critical',
    high: 'high',
    medium: 'medium',
    low: 'low',
  };
  return mapping[priority as string] || 'medium';
};

// Form validation schema
const projectEditSchema = z.object({
  name: z.string().min(1, 'Project name is required'),
  description: z.string().optional().or(z.literal('')),
  status: z.enum(['planning', 'active', 'on_hold', 'completed', 'cancelled'] as const),
  priority: z.enum(['low', 'medium', 'high', 'critical'] as const),
  startDate: z.any().nullable(),
  endDate: z.any().nullable(),
  estimatedMinutes: z.number().min(0).optional().nullable(),
  budgetedMinutes: z.number().min(0).optional().nullable(),
  riskLevel: z.enum(['low', 'medium', 'high'] as const),
  riskNotes: z.string().optional().or(z.literal('')),
  tags: z.array(z.string()).optional(),
  color: z.string().optional(),
});

type ProjectEditFormData = z.infer<typeof projectEditSchema>;

interface ProjectEditModalProps {
  open: boolean;
  onClose: () => void;
  project: Project;
}

// Predefined tag suggestions
const tagSuggestions = [
  'frontend',
  'backend',
  'api',
  'mobile',
  'infrastructure',
  'security',
  'performance',
  'ux',
  'urgent',
  'maintenance',
  'feature',
  'bug-fix',
  'research',
  'documentation',
];

// Project color options
const colorOptions = [
  { value: '#2563eb', label: 'Blue' },
  { value: '#16a34a', label: 'Green' },
  { value: '#dc2626', label: 'Red' },
  { value: '#ca8a04', label: 'Yellow' },
  { value: '#9333ea', label: 'Purple' },
  { value: '#0891b2', label: 'Cyan' },
  { value: '#ea580c', label: 'Orange' },
  { value: '#64748b', label: 'Gray' },
];

/**
 * ProjectEditModal
 * Modal for editing existing project details
 */
const ProjectEditModal: React.FC<ProjectEditModalProps> = ({
  open,
  onClose,
  project,
}) => {
  const queryClient = useQueryClient();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<ProjectEditFormData>({
    resolver: zodResolver(projectEditSchema),
    defaultValues: {
      name: project.name || '',
      description: project.description || '',
      status: (project.status as ProjectEditFormData['status']) || 'planning',
      priority: priorityEnumToFormValue(project.priority),
      startDate: project.startDate ? dayjs(project.startDate) : null,
      endDate: project.endDate ? dayjs(project.endDate) : null,
      estimatedMinutes: project.estimatedMinutes ?? null,
      budgetedMinutes: project.budgetedMinutes ?? null,
      riskLevel: project.riskLevel || 'low',
      riskNotes: project.riskNotes || '',
      tags: project.tags || [],
      color: project.color || '#2563eb',
    },
  });

  const startDate = watch('startDate');
  const selectedColor = watch('color');

  // Update project mutation
  const updateMutation = useMutation({
    mutationFn: (data: Partial<Project>) => projectService.updateProject(project.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.projects });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.project(project.id) });
      handleClose();
    },
  });

  // Reset form when modal opens or project changes
  useEffect(() => {
    if (open) {
      reset({
        name: project.name || '',
        description: project.description || '',
        status: (project.status as ProjectEditFormData['status']) || 'planning',
        priority: priorityEnumToFormValue(project.priority),
        startDate: project.startDate ? dayjs(project.startDate) : null,
        endDate: project.endDate ? dayjs(project.endDate) : null,
        estimatedMinutes: project.estimatedMinutes ?? null,
        budgetedMinutes: project.budgetedMinutes ?? null,
        riskLevel: project.riskLevel || 'low',
        riskNotes: project.riskNotes || '',
        tags: project.tags || [],
        color: project.color || '#2563eb',
      });
    }
  }, [open, project, reset]);

  const onSubmit = async (data: ProjectEditFormData) => {
    const payload: Partial<Project> = {
      name: data.name,
      description: data.description || '',
      status: data.status === 'planning' ? ProjectStatus.PLANNING
        : data.status === 'active' ? ProjectStatus.ACTIVE
        : data.status === 'on_hold' ? ProjectStatus.ON_HOLD
        : data.status === 'completed' ? ProjectStatus.COMPLETED
        : data.status === 'cancelled' ? ProjectStatus.CANCELLED
        : ProjectStatus.PLANNING,
      priority: data.priority as any,
      startDate: data.startDate ? dayjs(data.startDate).format('YYYY-MM-DD') : project.startDate,
      endDate: data.endDate ? dayjs(data.endDate).format('YYYY-MM-DD') : project.endDate,
      estimatedMinutes: data.estimatedMinutes ?? undefined,
      budgetedMinutes: data.budgetedMinutes ?? undefined,
      riskLevel: data.riskLevel,
      riskNotes: data.riskNotes || '',
      tags: data.tags || [],
      color: data.color,
    };

    updateMutation.mutate(payload);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '90vh' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Edit Project
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Modify project details and settings
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Error Alert */}
              {updateMutation.isError && (
                <Alert severity="error" icon={<AlertCircle size={20} />}>
                  Failed to update project. Please try again.
                </Alert>
              )}

              {/* Basic Information Section */}
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                Basic Information
              </Typography>

              {/* Project Name */}
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Project Name"
                    placeholder="Enter project name"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    fullWidth
                    required
                  />
                )}
              />

              {/* Description */}
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Description"
                    placeholder="Describe the project objectives and scope"
                    multiline
                    rows={3}
                    error={!!errors.description}
                    helperText={errors.description?.message}
                    fullWidth
                  />
                )}
              />

              {/* Status and Priority */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.status}>
                      <InputLabel>Status</InputLabel>
                      <Select {...field} label="Status">
                        <MenuItem value="planning">Planning</MenuItem>
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="on_hold">On Hold</MenuItem>
                        <MenuItem value="completed">Completed</MenuItem>
                        <MenuItem value="cancelled">Cancelled</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />

                <Controller
                  name="priority"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.priority}>
                      <InputLabel>Priority</InputLabel>
                      <Select {...field} label="Priority">
                        <MenuItem value="low">Low</MenuItem>
                        <MenuItem value="medium">Medium</MenuItem>
                        <MenuItem value="high">High</MenuItem>
                        <MenuItem value="critical">Critical</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Box>

              <Divider />

              {/* Timeline Section */}
              <Typography variant="subtitle2" color="text.secondary">
                Timeline
              </Typography>

              {/* Dates */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Controller
                  name="startDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      label="Start Date"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.startDate,
                          helperText: errors.startDate?.message as string,
                        },
                      }}
                    />
                  )}
                />

                <Controller
                  name="endDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      label="End Date"
                      minDate={startDate ? dayjs(startDate) : undefined}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.endDate,
                          helperText: errors.endDate?.message as string,
                        },
                      }}
                    />
                  )}
                />
              </Box>

              {/* Hours */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Controller
                  name="estimatedMinutes"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                      label="Estimated Hours"
                      type="number"
                      fullWidth
                      InputProps={{ inputProps: { min: 0 } }}
                      error={!!errors.estimatedMinutes}
                      helperText={errors.estimatedMinutes?.message}
                    />
                  )}
                />

                <Controller
                  name="budgetedMinutes"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                      label="Budgeted Hours"
                      type="number"
                      fullWidth
                      InputProps={{ inputProps: { min: 0 } }}
                      error={!!errors.budgetedMinutes}
                      helperText={errors.budgetedMinutes?.message}
                    />
                  )}
                />
              </Box>

              <Divider />

              {/* Risk Section */}
              <Typography variant="subtitle2" color="text.secondary">
                Risk Assessment
              </Typography>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Controller
                  name="riskLevel"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.riskLevel}>
                      <InputLabel>Risk Level</InputLabel>
                      <Select {...field} label="Risk Level">
                        <MenuItem value="low">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                bgcolor: '#16a34a',
                              }}
                            />
                            Low Risk
                          </Box>
                        </MenuItem>
                        <MenuItem value="medium">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                bgcolor: '#ca8a04',
                              }}
                            />
                            Medium Risk
                          </Box>
                        </MenuItem>
                        <MenuItem value="high">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                bgcolor: '#dc2626',
                              }}
                            />
                            High Risk
                          </Box>
                        </MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Box>

              <Controller
                name="riskNotes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Risk Notes"
                    placeholder="Document potential risks and mitigation strategies"
                    multiline
                    rows={2}
                    fullWidth
                    error={!!errors.riskNotes}
                    helperText={errors.riskNotes?.message}
                  />
                )}
              />

              <Divider />

              {/* Metadata Section */}
              <Typography variant="subtitle2" color="text.secondary">
                Organization & Display
              </Typography>

              {/* Tags */}
              <Controller
                name="tags"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    multiple
                    freeSolo
                    options={tagSuggestions}
                    value={field.value || []}
                    onChange={(_, newValue) => {
                      field.onChange(newValue);
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option}
                          label={option}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tags"
                        placeholder="Add tags for organization"
                        error={!!errors.tags}
                        helperText="Press Enter to add custom tags"
                      />
                    )}
                  />
                )}
              />

              {/* Color */}
              <Controller
                name="color"
                control={control}
                render={({ field }) => (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Project Color
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {colorOptions.map((color) => (
                        <Box
                          key={color.value}
                          onClick={() => field.onChange(color.value)}
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            bgcolor: color.value,
                            cursor: 'pointer',
                            border: selectedColor === color.value ? '3px solid' : '2px solid transparent',
                            borderColor: selectedColor === color.value ? 'primary.main' : 'transparent',
                            outline: selectedColor === color.value ? '2px solid white' : 'none',
                            transition: 'all 0.2s',
                            '&:hover': {
                              transform: 'scale(1.1)',
                            },
                          }}
                          title={color.label}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              />
            </Box>
          </LocalizationProvider>
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleClose} variant="outlined" color="inherit">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting || updateMutation.isPending || !isDirty}
            startIcon={<Save size={18} />}
          >
            {(isSubmitting || updateMutation.isPending) ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ProjectEditModal;
